import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '@/config';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HorizontalStepTracker } from '@/components/shared/HorizontalStepTracker';
import { ArrowLeft, Building2, MapPin, Users, FileText, Download, Eye, File } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { getDocuments } from '@/services/uploadService';

export default function LLCRequestDetailView() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [llc, setLlc] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusSteps = [
    { label: 'Submitted', status: 'completed' },
    { label: 'In Review', status: llc?.status === 'draft' ? 'pending' : 'completed' },
    { label: 'Processing', status: ['submitted', 'in_review'].includes(llc?.status) ? 'current' : llc?.status === 'completed' ? 'completed' : 'pending' },
    { label: 'Completed', status: llc?.status === 'completed' ? 'completed' : 'pending' },
  ];

  useEffect(() => {
    if (user && requestId) {
      fetchLLCDetail();
      fetchDocuments();
    }
  }, [user, requestId]);

  const fetchDocuments = async () => {
    try {
      const docs = await getDocuments('llc_creation', requestId);
      setDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleViewDocument = (url, filename) => {
    window.open(url, '_blank');
  };

  const handleDownloadDocument = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const fetchLLCDetail = async () => {
    try {
      setLoading(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${backendUrl}/api/llc-formations/${requestId}/detail`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      
      // Parse JSON fields if they're strings
      if (typeof data.company_info === 'string') {
        try {
          data.company_info = JSON.parse(data.company_info);
        } catch (e) {
          console.error('Error parsing company_info:', e);
          data.company_info = {};
        }
      }
      
      if (typeof data.members === 'string') {
        try {
          data.members = JSON.parse(data.members);
        } catch (e) {
          console.error('Error parsing members:', e);
          data.members = {};
        }
      }
      
      if (typeof data.registered_agent === 'string') {
        try {
          data.registered_agent = JSON.parse(data.registered_agent);
        } catch (e) {
          console.error('Error parsing registered_agent:', e);
          data.registered_agent = null;
        }
      }

      setLlc(data);
    } catch (error) {
      console.error('Error fetching LLC detail:', error);
      toast.error('Failed to load LLC details');
      setLlc(null);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="LLC Request Details" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!llc) {
    return (
      <DashboardLayout role={user.role} title="LLC Request Details" user={user}>
        <div className="text-center py-12">
          <Building2 className="h-16 w-16 mx-auto text-muted-foreground" />
          <h3 className="mt-4 text-lg font-semibold">LLC request not found</h3>
          <Button className="mt-4" onClick={() => navigate('/llc/requests')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Requests
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="LLC Request Details" user={user}>
      <div className="space-y-6">
        <Breadcrumbs items={[
          { label: 'LLC', href: '/llc/dashboard' },
          { label: 'My Requests', href: '/llc/requests' },
          { label: 'Details' }
        ]} />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{llc.company_name}</h2>
            <p className="text-muted-foreground">LLC Formation Request</p>
          </div>
          <div className="flex gap-2">
            {(llc.status === 'DRAFT' || llc.status === 'draft' || llc.status === 'NEED_INFO') && (
              <Button 
                className="btn-professional"
                onClick={() => navigate(`/llc/create?edit=${requestId}`)}
              >
                {llc.status === 'NEED_INFO' ? 'Edit & Provide Info' : 'Continue Editing'}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/llc/requests')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Requests
            </Button>
          </div>
        </div>

        {/* Status Timeline */}
        <Card>
          <CardHeader>
            <CardTitle>Formation Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <HorizontalStepTracker steps={statusSteps} />
          </CardContent>
        </Card>

        {/* Company Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Company Name</p>
                <p className="font-semibold text-lg">{llc.company_name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge className="text-sm">{llc.status}</Badge>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">State of Formation</p>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <p className="font-medium">{llc.state}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Business Type</p>
                <p className="font-medium">{llc.business_type || 'Not specified'}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Submitted Date</p>
                <p className="font-medium">{new Date(llc.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <p className="font-medium">{new Date(llc.updated_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Details */}
        {llc.company_info && Object.keys(llc.company_info).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(llc.company_info)
                  .filter(([key, value]) => {
                    // Filter out complex objects, arrays, and special fields
                    if (['selectedPackage', 'members', 'alternateNames'].includes(key)) return false;
                    if (typeof value === 'object' && value !== null) return false;
                    if (Array.isArray(value)) return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </p>
                      <p className="font-medium">{value || 'N/A'}</p>
                    </div>
                  ))}
                
                {/* Alternate Names - handle array separately */}
                {llc.company_info.alternateNames && Array.isArray(llc.company_info.alternateNames) && llc.company_info.alternateNames.length > 0 && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground">Alternate Names</p>
                    <p className="font-medium">{llc.company_info.alternateNames.join(', ')}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Management Structure */}
        {llc.members && llc.members.managementStructure && (
          <Card>
            <CardHeader>
              <CardTitle>Management Structure</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium capitalize">
                {llc.members.managementStructure.replace(/_/g, ' ')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Members Information */}
        {llc.members && llc.members.members && Array.isArray(llc.members.members) && llc.members.members.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                LLC Members & Owners
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {llc.members.members.map((member, index) => (
                  <div key={member.id || index} className="p-4 border rounded-lg">
                    <p className="font-semibold mb-2">Member {index + 1}</p>
                    <div className="grid gap-2 md:grid-cols-2">
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium">{member.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Ownership %</p>
                        <p className="text-sm">{member.ownershipPercentage || '0'}%</p>
                      </div>
                      {member.address && (
                        <div className="md:col-span-2">
                          <p className="text-xs text-muted-foreground">Address</p>
                          <p className="text-sm">{member.address}</p>
                        </div>
                      )}
                      {member.isManager && (
                        <div className="md:col-span-2">
                          <Badge variant="secondary">Designated Manager</Badge>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Registered Agent */}
        {llc.registered_agent && typeof llc.registered_agent === 'object' && Object.keys(llc.registered_agent).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Registered Agent Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(llc.registered_agent)
                  .filter(([key, value]) => {
                    // Filter out complex nested objects
                    if (typeof value === 'object' && value !== null) return false;
                    return true;
                  })
                  .map(([key, value]) => (
                    <div key={key}>
                      <p className="text-sm text-muted-foreground capitalize">{key}</p>
                      <p className="font-medium">{value || 'N/A'}</p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <File className="h-5 w-5" />
              Uploaded Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No documents uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{doc.file_name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {doc.file_type && <Badge variant="outline" className="text-xs">{doc.file_type}</Badge>}
                          <span>{doc.file_size && `${(doc.file_size / 1024).toFixed(1)} KB`}</span>
                          <span>{doc.uploaded_at && `• ${new Date(doc.uploaded_at).toLocaleDateString()}`}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDocument(doc.file_url, doc.file_name)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadDocument(doc.file_url, doc.file_name)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Button */}
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Need help with your LLC?</p>
                <p className="text-sm text-muted-foreground">Contact our support team for assistance</p>
              </div>
              <Button>Contact Support</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
